import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';

const trainingAssignmentTrackingLabels = getLabels('Training Assignment');
const trainingAssignmentTrackingBase = createTrackingConfig('Training Assignment');

export const trainingAssignmentConfig = {
  entityName: 'TrainingAssignment',
  
  // Entity information for file attachments (if needed in future)
  entityTypeId: null, // Training assignments don't typically have file attachments
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/training-assignment',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete'
    },
    lookupEndpoints: {
      employees: '/employee',
      trainings: '/training',
      trainingStatuses: '/training-status'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/training-assignment'
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '30%', // 30% of table width 
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
    tableVariant: 'default', // 'default' | 'modern' | 'cards' | 'board' | 'timeline' 
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth // From app config: '60%'
  },

  // Features configuration
  features: {
    fileAttachments: false, // Disable file attachment functionality for training assignments
    masterEntityTypeId: 10, // Training Assignment entity type ID for audit logs
  },
  
  // Custom Split View Configuration
  customSplitView: {
    enabled: true,
    component: 'CustomTrainingAssignmentSplitView',
    importPath: './CustomTrainingAssignmentSplitView'
  },
  
  // Custom handlers for Training Assignment-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in trainingAssignmentPage
    onDelete: null, // Will be defined in trainingAssignmentPage
    onLoad: null, // For any special loading logic
    onFormOpen: null, // Will be defined in trainingAssignmentPage
    
    // Training Assignment-specific actions
    onMarkComplete: null, // Will handle marking assignment as complete
    onMarkIncomplete: null, // Will handle marking assignment as incomplete
    onEscalate: null, // Will handle assignment escalation
    
    // Board functionality (if timeline/board views are needed)
    onBoardDrop: async ({ item, fromGroup, toGroup }) => {
      if (toGroup === fromGroup) return;
      await fetch(`/training-assignment/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statusId: Number(toGroup) }),
      });
      // Optionally refresh list afterward
    }
  },
  
  // Service configuration (for reference; actual services are generic)
  service: {
    name: 'trainingAssignmentService',
    methods: {
      getAll: 'getAllTrainingAssignments',
      getById: 'getTrainingAssignmentById', 
      create: 'createTrainingAssignment',
      update: 'updateTrainingAssignment',
      delete: 'deleteTrainingAssignment',
      validate: 'validateData'
    }
  },
  
  // Display Configuration
  displayName: 'Training Assignment Management',
  description: 'Training assignment management for document training compliance and tracking',
  icon: 'M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z',
  
  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    'id', 'employee', 'training', 'status',
    'createdBy', 'updatedBy', 'createdById', 'updatedById',
    'createdAt', 'updatedAt',
  ],

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'employeeId',
      label: 'Employee',
      type: 'select',
      required: true,
      lookupEndpoint: 'employees',
      options: [],
      table: { 
        header: 'Employee', 
        width: '180px', 
        sortable: true,
        displayField: 'employee'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid employee'
      }
    },
    {
      name: 'trainingId',
      label: 'Training',
      type: 'select',
      required: true,
      lookupEndpoint: 'trainings',
      options: [],
      table: { 
        header: 'Document', 
        width: '220px', 
        sortable: true,
        displayField: 'document'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid document'
      }
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: true,
      lookupEndpoint: 'trainingStatuses',
      options: [],
      table: { 
        header: 'Status', 
        width: '120px', 
        sortable: true,
        displayField: 'status',
        cellRenderer: 'statusBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid status'
      }
    },
    {
      name: 'assignedDate',
      label: 'Assigned Date',
      type: 'date',
      required: true,
      table: { 
        header: 'Assigned', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        required: true,
        message: 'Assigned date is required'
      }
    },
    {
      name: 'dueDate',
      label: 'Due Date',
      type: 'date',
      required: true,
      table: { 
        header: 'Due', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        required: true,
        message: 'Due date is required'
      }
    },
    {
      name: 'completedDate',
      label: 'Completed Date',
      type: 'date',
      table: { 
        header: 'Completed', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    {
      name: 'escalatedAt',
      label: 'Escalated Date',
      type: 'date',
      table: { 
        header: 'Escalated', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    {
      name: 'closedAt',
      label: 'Closed Date',
      type: 'date',
      table: { 
        header: 'Closed', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    {
      name: 'lastReminderAt',
      label: 'Last Reminder Date',
      type: 'date',
      table: { 
        header: 'Last Reminder', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    {
      name: 'reminderCount',
      label: 'Reminder Count',
      type: 'number',
      table: { 
        header: 'Reminders', 
        width: '100px',
        sortable: true,
        cellRenderer: 'number'
      },
      filterable: true,
      filterType: 'numberRange',
      validation: {
        min: 0,
        message: 'Reminder count must be 0 or greater'
      }
    },
    {
      name: 'score',
      label: 'Score',
      type: 'number',
      table: { 
        header: 'Score', 
        width: '80px',
        sortable: true,
        cellRenderer: 'number'
      },
      filterable: true,
      filterType: 'numberRange',
      validation: {
        min: 0,
        max: 100,
        message: 'Score must be between 0 and 100'
      }
    },
    {
      name: 'scoreComments',
      label: 'Score Comments',
      type: 'textarea',
      table: false,
      filterable: false,
      validation: {
        maxLength: 500,
        message: 'Score comments must be less than 500 characters'
      }
    },
    {
      name: 'trainingNotes',
      label: 'Training Notes',
      type: 'textarea',
      table: false,
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Training notes must be less than 1000 characters'
      }
    },
  ],
  
  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name
      }));
    
    return columns;
  },

  exportFileName: 'qms_training_assignments',
  
  // Backend field mappings
  fieldMappings: {
    user: 'user.userDisplayName',
    document: 'document.title',
    status: 'status.name',
    createdBy: 'createdBy.userDisplayName',
    updatedBy: 'updatedBy.userDisplayName'
  },
  
  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Custom Sort Orders
  customSortOrders: {
    statusId: ['pending', 'in-progress', 'completed', 'overdue'], // Status priority
    assignedDate: 'desc' // Most recent first
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'employeeId', 'trainingId', 'statusId'
      ]
    },
    {
      name: 'dates',
      label: 'Dates',
      icon: '📅',
      fields: [
        'assignedDate', 'dueDate', 'completedDate', 'escalatedAt'
      ]
    }
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Assignment Details',
      icon: '👥',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['employeeId', 'statusId', 'trainingId'],
      fieldSpans: {
        'employeeId': 'md:col-span-8',
        'statusId': 'md:col-span-4',       
        'trainingId': 'md:col-span-12',
      }      
    },
    {
      title: 'Dates',
      icon: '📅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['assignedDate', 'dueDate', 'completedDate', 'closedAt', 'escalatedAt', 'lastReminderAt']
    },
    {
      title: 'Assessment & Scoring',
      icon: '📊',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['score', 'scoreComments', 'reminderCount'],
      fieldSpans: {
        'score': 'md:col-span-4',
        'reminderCount': 'md:col-span-4',
        'scoreComments': 'md:col-span-12',
      }
    },
    {
      title: 'Training Notes',
      icon: '📝',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['trainingNotes'],
      fieldSpans: {
        'trainingNotes': 'md:col-span-12',
      }
    },
  ],

  // Tracking — show audit users by display name (nested objects from API / selected row)
  trackingSection: {
    ...trainingAssignmentTrackingBase,
    fields: trainingAssignmentTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: trainingAssignmentTrackingLabels.createdDateLabel,
            userEmptyText: trainingAssignmentTrackingLabels.notSpecifiedText,
            dateEmptyText: trainingAssignmentTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: trainingAssignmentTrackingLabels.updatedDateLabel,
              userEmptyText: trainingAssignmentTrackingLabels.notModifiedText,
              dateEmptyText: trainingAssignmentTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Labels and Text Configuration (uses predefined Training Assignment labels + custom overrides)
  labels: {
    // Using predefined Training Assignment labels from centralized labels file
    // You can override any label by uncommenting and modifying:
    singular: 'Training Assignment',
    plural: 'Training Assignments',
    pageTitle: 'Training Assignment Management',
    pageSubtitle: 'Manage and track document training assignments for compliance',
    searchPlaceholder: 'Search training assignments by user or document...',
    viewTitle: 'View Training Assignment',
    noSelectionText: 'Select a training assignment to see details',
    
    // Action labels
    markComplete: 'Mark Complete',
    markIncomplete: 'Mark Incomplete',
    escalate: 'Escalate Assignment',
    viewProgress: 'View Progress'
  },

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    // Convert foreign key fields to integers or remove if empty
    const foreignKeyFields = ['employeeId', 'trainingId', 'statusId'];
    foreignKeyFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert date fields to proper Date objects
    const dateFields = ['assignedDate', 'dueDate', 'completedDate', 'escalatedAt'];
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });
    
    return transformed;
  },

  // Notification messages
  messages: {
    loading: 'Loading training assignments...',
    noData: 'No training assignments found. Create your first training assignment to get started.',
    loadError: 'Failed to load training assignments',
    saveSuccess: 'Training assignment saved successfully!',
    saveError: 'Failed to save training assignment',
    deleteSuccess: 'Training assignment deleted successfully!',
    deleteError: 'Failed to delete training assignment',
    deleteConfirm: 'Are you sure you want to delete this training assignment? This action cannot be undone.',
    markCompleteSuccess: 'Training assignment marked as complete!',
    markIncompleteSuccess: 'Training assignment marked as incomplete!',
    escalateSuccess: 'Training assignment escalated successfully!'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Help text for field descriptions
  helpText: {
    employeeId: { short: 'Select the employee who needs to complete this training assignment.' },
    trainingId: { short: 'Select the training that the employee needs to complete.' },
    statusId: { short: 'Current status of the training assignment (pending, in-progress, completed, etc.).' },
    assignedDate: { short: 'Date when the training assignment was created and assigned to the user.' },
    dueDate: { short: 'Deadline by which the training must be completed.' },
    completedDate: { short: 'Date when the user completed the training assignment.' },
    escalatedAt: { short: 'Date when the assignment was escalated due to overdue status.' },
    closedAt: { short: 'Date when the training assignment was officially closed.' },
    lastReminderAt: { short: 'Date when the last reminder was sent to the user about this assignment.' },
    reminderCount: { short: 'Number of reminders sent to the user for this training assignment.' },
    score: { short: 'Training assessment score (0-100) if applicable.' },
    scoreComments: { short: 'Comments about the training assessment or score.' },
    trainingNotes: { short: 'Additional notes about the training assignment, progress, or special considerations.' }
  }
};

export default trainingAssignmentConfig;

